import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import SmartLogo from "../components/SmartLogo";

const formatErr = (d) => {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
};

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#FDFDFD] px-6 sm:px-12 py-10" data-testid="forgot-password-page">
      <div className="w-full max-w-md mx-auto">
        <div className="mb-12">
          <SmartLogo />
        </div>

        {sent ? (
          <div className="border border-green-200 bg-green-50 p-6 rounded-sm" data-testid="forgot-success">
            <CheckCircle2 size={20} className="text-green-700 mb-2" />
            <h2 className="font-display text-xl font-bold tracking-tight mb-1">Check your inbox</h2>
            <p className="text-sm text-zinc-700">
              If an account exists for <span className="font-mono">{email}</span>, we've sent a password reset link.
              It expires in 1 hour.
            </p>
            <Link to="/login" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold underline text-zinc-950">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <span className="label-eyebrow">Account recovery</span>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-3 leading-[1.05]">
              Forgot your password?
            </h1>
            <p className="text-sm text-zinc-600 mt-2">
              Enter your email and we'll send you a link to set a new one.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <label className="label-eyebrow block mb-1.5">Email</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="email" required value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="forgot-email"
                    className="w-full pl-9 pr-3 py-2.5 border border-zinc-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-1"
                  />
                </div>
              </div>
              <button type="submit" disabled={busy} data-testid="forgot-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-zinc-950 text-white font-semibold px-5 py-3 rounded-sm hover:bg-zinc-800 disabled:opacity-50">
                {busy ? "Sending…" : "Send reset link"} <ArrowRight size={16} />
              </button>
              <Link to="/login" className="block text-sm text-center text-zinc-600 hover:text-zinc-950 underline pt-2">
                Back to sign in
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
